// "buildx-pytha — usage & contracts by company". Sortable table over the enriched
// /api/pytha/companies (PythaCompany[]) — now returning real per-company R2 storage,
// renders and jobs alongside CF cost, rebill, plan and expiry + days-left. Per-row
// "ต่ออายุ" renews +12 months then refetches. Row click drills (stub). Self-fetching;
// loading / empty / error + dark mode. No props.
import { useEffect, useMemo, useState } from "react";
import { api, ApiError, type PythaCompany, type PythaDailyRow } from "../lib/api";
import { Card, SectionTitle } from "./ui";
import { Modal } from "./Modal";
import { usd, gb as gbFmt } from "../lib/format";

type SortKey = "expiry" | "rebill" | "cost" | "jobs" | "name";

/** Storage value → "47 GB" / "4.3 GB" (one decimal under 10). */
function gb(n: number | null | undefined): string {
  const v = typeof n === "number" && Number.isFinite(n) ? n : 0;
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} GB`;
}

/** YYYY-MM-DD (or ISO) → "Jun 7, 2026" or "—". */
function fdate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** Tailwind classes for a days-left pill: red ≤7, amber ≤30, else neutral. */
function dueClasses(daysLeft: number | null): string {
  if (daysLeft === null) return "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300";
  if (daysLeft <= 7) return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300";
  if (daysLeft <= 30) return "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300";
  return "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300";
}

function dueLabel(daysLeft: number | null): string {
  if (daysLeft === null) return "—";
  if (daysLeft < 0) return "หมดอายุ";
  return `${daysLeft} วัน`;
}

export default function PythaTable({ refreshTick }: { refreshTick?: number }) {
  const [rows, setRows] = useState<PythaCompany[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortKey>("expiry");
  const [renewing, setRenewing] = useState<string | null>(null);
  const [drillCompany, setDrillCompany] = useState<PythaCompany | null>(null);

  async function load() {
    setError(null);
    try {
      const r = await api.pythaCompanies();
      setRows(r);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : "Failed to load companies");
    }
  }

  // Refetch on mount and whenever Dashboard's refreshTick bumps (60s auto-refresh
  // or manual refresh). Existing rows stay on screen while fresh data loads.
  useEffect(() => {
    let alive = true;
    setError(null);
    api
      .pythaCompanies()
      .then((r) => {
        if (alive) setRows(r);
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof ApiError ? e.message : "Failed to load companies");
      });
    return () => {
      alive = false;
    };
  }, [refreshTick]);

  const sorted = useMemo(() => {
    const list = [...(rows ?? [])];
    const dleft = (c: PythaCompany) => (c.days_left ?? Number.POSITIVE_INFINITY);
    list.sort((a, b) => {
      switch (sort) {
        case "rebill":
          return b.rebill_usd - a.rebill_usd;
        case "cost":
          return b.attributed_cost_usd - a.attributed_cost_usd;
        case "jobs":
          return b.jobs - a.jobs;
        case "name":
          return a.company_name.localeCompare(b.company_name);
        case "expiry":
        default:
          return dleft(a) - dleft(b);
      }
    });
    return list;
  }, [rows, sort]);

  const urgent = (rows ?? []).filter((c) => c.days_left !== null && c.days_left <= 7).length;
  const soon = (rows ?? []).filter(
    (c) => c.days_left !== null && c.days_left <= 30,
  ).length;

  const totalStorage = (rows ?? []).reduce((s, c) => s + c.storage_gb, 0);
  const totalRenders = (rows ?? []).reduce((s, c) => s + c.renders, 0);
  const totalJobs = (rows ?? []).reduce((s, c) => s + c.jobs, 0);
  const totalCost = (rows ?? []).reduce((s, c) => s + c.attributed_cost_usd, 0);
  const totalRebill = (rows ?? []).reduce((s, c) => s + c.rebill_usd, 0);

  async function renew(companyId: string) {
    setRenewing(companyId);
    try {
      await api.renewContract(companyId, 12, 0);
      await load();
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : "Renew failed");
    } finally {
      setRenewing(null);
    }
  }

  return (
    <section>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <SectionTitle>buildx-pytha — usage &amp; contracts by company</SectionTitle>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
          className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
        >
          <option value="expiry">Sort: expiring soonest</option>
          <option value="rebill">Sort: rebill amount</option>
          <option value="cost">Sort: CF cost</option>
          <option value="jobs">Sort: jobs</option>
          <option value="name">Sort: name</option>
        </select>
      </div>

      {rows && rows.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2 text-xs">
          <span className="inline-flex rounded-full bg-red-100 px-2 py-0.5 font-medium text-red-800 dark:bg-red-900/40 dark:text-red-300">
            {urgent} ≤7 days
          </span>
          <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
            {soon} ≤30 days
          </span>
        </div>
      )}

      <Card className="overflow-x-auto p-0">
        {error ? (
          <div className="p-4 text-sm text-red-600 dark:text-red-400">⚠️ {error}</div>
        ) : rows === null ? (
          <div className="p-4 text-sm text-gray-400 dark:text-gray-500">Loading…</div>
        ) : sorted.length === 0 ? (
          <div className="p-4 text-sm text-gray-500 dark:text-gray-400">
            No companies yet — seed pytha_contract.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:text-gray-400">
                <th className="px-4 py-2 font-medium">Company</th>
                <th className="px-4 py-2 text-right font-medium">R2</th>
                <th className="px-4 py-2 text-right font-medium">Renders</th>
                <th className="px-4 py-2 text-right font-medium">Jobs</th>
                <th className="px-4 py-2 text-right font-medium">CF cost</th>
                <th className="px-4 py-2 text-right font-medium">Rebill</th>
                <th className="px-4 py-2 font-medium">Plan</th>
                <th className="px-4 py-2 font-medium">Expires</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => (
                <tr
                  key={c.company_id}
                  onClick={() => setDrillCompany(c)}
                  className="cursor-pointer border-b border-gray-100 last:border-0 hover:bg-gray-50 dark:border-gray-700/60 dark:hover:bg-gray-700/40"
                >
                  <td className="px-4 py-2 font-semibold text-gray-900 dark:text-gray-100">
                    {c.company_name}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">
                    {gb(c.storage_gb)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">
                    {c.renders.toLocaleString("en-US")}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">
                    {c.jobs.toLocaleString("en-US")}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">
                    {usd(c.attributed_cost_usd)}
                  </td>
                  <td
                    className={
                      "px-4 py-2 text-right font-semibold tabular-nums " +
                      (c.rebill_usd > 0
                        ? "text-red-600 dark:text-red-400"
                        : "text-gray-400 dark:text-gray-500")
                    }
                  >
                    {c.rebill_usd > 0 ? `+${usd(c.rebill_usd)}` : "—"}
                  </td>
                  <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{c.plan ?? "—"}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-gray-700 dark:text-gray-300">
                    {fdate(c.expiry_date)}{" "}
                    <span
                      className={
                        "ml-1 inline-flex rounded-full px-2 py-0.5 text-xs font-medium " +
                        dueClasses(c.days_left)
                      }
                    >
                      {dueLabel(c.days_left)}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        renew(c.company_id);
                      }}
                      disabled={renewing === c.company_id}
                      className={
                        "rounded-md border px-2 py-1 text-xs font-medium transition-colors disabled:opacity-50 " +
                        (c.days_left !== null && c.days_left <= 30
                          ? "border-blue-600 bg-blue-600 text-white hover:bg-blue-700"
                          : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700")
                      }
                    >
                      {renewing === c.company_id ? "…" : "ต่ออายุ"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200 bg-gray-50 text-sm font-medium dark:border-gray-700 dark:bg-gray-800/60">
                <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                  Total — {sorted.length} companies
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-gray-900 dark:text-gray-100">
                  {gb(totalStorage)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-gray-900 dark:text-gray-100">
                  {totalRenders.toLocaleString("en-US")}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-gray-900 dark:text-gray-100">
                  {totalJobs.toLocaleString("en-US")}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-gray-900 dark:text-gray-100">
                  {usd(totalCost)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-red-600 dark:text-red-400">
                  {totalRebill > 0 ? `+${usd(totalRebill)}` : "—"}
                </td>
                <td colSpan={3}></td>
              </tr>
            </tfoot>
          </table>
        )}
      </Card>

      {drillCompany && (
        <CompanyDrillModal company={drillCompany} onClose={() => setDrillCompany(null)} />
      )}
    </section>
  );
}

// ── Company drill modal ──────────────────────────────────────────────
type DrillState =
  | { phase: "loading" }
  | { phase: "error"; message: string }
  | { phase: "ready"; rows: PythaDailyRow[] };

/** Read a numeric field defensively from camelCase or snake_case D1 output. */
function num(row: Record<string, unknown>, ...keys: string[]): number {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === "number" && Number.isFinite(v)) return v;
  }
  return 0;
}

function CompanyDrillModal({
  company,
  onClose,
}: {
  company: PythaCompany;
  onClose: () => void;
}) {
  const [state, setState] = useState<DrillState>({ phase: "loading" });

  useEffect(() => {
    let alive = true;
    setState({ phase: "loading" });
    api
      .pythaCompany(company.company_id)
      .then((rows) => {
        if (!alive) return;
        // Newest day first.
        const sortedRows = [...(rows ?? [])].sort((a, b) =>
          String(b.day).localeCompare(String(a.day)),
        );
        setState({ phase: "ready", rows: sortedRows });
      })
      .catch((e: unknown) => {
        if (alive)
          setState({
            phase: "error",
            message: e instanceof ApiError ? e.message : "Failed to load daily usage",
          });
      });
    return () => {
      alive = false;
    };
  }, [company.company_id]);

  return (
    <Modal title={company.company_name} subtitle="Daily usage" onClose={onClose}>
      {state.phase === "loading" && (
        <div className="py-6 text-sm text-gray-400 dark:text-gray-500">Loading…</div>
      )}

      {state.phase === "error" && (
        <div className="py-6 text-sm text-red-600 dark:text-red-400">⚠️ {state.message}</div>
      )}

      {state.phase === "ready" && state.rows.length === 0 && (
        <div className="py-6 text-sm text-gray-500 dark:text-gray-400">
          No daily usage recorded yet.
        </div>
      )}

      {state.phase === "ready" && state.rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:text-gray-400">
                <th className="py-2 pr-3 font-medium">Day</th>
                <th className="px-3 py-2 text-right font-medium">Jobs</th>
                <th className="px-3 py-2 text-right font-medium">Renders</th>
                <th className="px-3 py-2 text-right font-medium">R2</th>
                <th className="px-3 py-2 text-right font-medium">CF cost</th>
                <th className="py-2 pl-3 text-right font-medium">Rebill</th>
              </tr>
            </thead>
            <tbody>
              {state.rows.map((r, i) => {
                const row = r as unknown as Record<string, unknown>;
                const jobs = num(row, "requests");
                const renders = num(row, "renders");
                const storage = num(row, "r2_storage_gb", "r2StorageGb");
                const cost = num(row, "attributed_cost_usd", "attributedCostUsd");
                const rebill = num(row, "rebill_usd", "rebillUsd");
                return (
                  <tr
                    key={`${r.day}-${i}`}
                    className="border-b border-gray-100 last:border-0 dark:border-gray-700/60"
                  >
                    <td className="whitespace-nowrap py-2 pr-3 font-medium text-gray-900 dark:text-gray-100">
                      {fdate(r.day)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">
                      {Math.round(jobs).toLocaleString("en-US")}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">
                      {Math.round(renders).toLocaleString("en-US")}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">
                      {gbFmt(storage)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">
                      {usd(cost)}
                    </td>
                    <td
                      className={
                        "py-2 pl-3 text-right font-semibold tabular-nums " +
                        (rebill > 0
                          ? "text-red-600 dark:text-red-400"
                          : "text-gray-400 dark:text-gray-500")
                      }
                    >
                      {rebill > 0 ? `+${usd(rebill)}` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
